import { z } from "zod";
import i18next from "i18next";
import {
  FACEBOOK as FACEBOOK_CONSTANT,
  INSTAGRAM as INSTAGRAM_CONSTANT,
  TIKTOK as TIKTOK_CONSTANT,
  YOUTUBE as YOUTUBE_CONSTANT,
  LINKEDIN as LINKEDIN_CONSTANT,
} from "../../../../utils/constants";
import { FIELD_REQUIRED } from "../../../../i18n/keysTranslations";
import { isGeneralDescriptionRequired } from "../utils/contentTypeSync";

const requiredMessage = i18next.t(FIELD_REQUIRED);

export const getShareModalSchema = ({ selectedPlatforms }) =>
  z
    .object({
      language: z.string().min(1, requiredMessage),
      description: z.string().optional(),
      scheduledDate: z.date().nullable().optional(),
      platforms: z.array(z.string()).min(1, requiredMessage),
      platformsData: z.object({
        facebook: z
          .object({
            account: z.any().nullable(),
            contentType: z.string().optional(),
          })
          .optional(),
        instagram: z
          .object({
            account: z.any().nullable(),
            contentType: z.string().optional(),
            collaborators: z.array(z.string()).max(3).optional(),
          })
          .optional(),
        tiktok: z
          .object({
            account: z.any().nullable(),
            privacy_level: z.string().optional(),
            contentType: z.string().optional(),
            disable_duet: z.boolean().optional(),
            disable_stitch: z.boolean().optional(),
            disable_comment: z.boolean().optional(),
            is_aigc: z.boolean().optional(),
            auto_add_music: z.boolean().optional(),
          })
          .optional(),
        youtube: z
          .object({
            account: z.any().nullable(),
            title: z.string().optional(),
            privacyStatus: z.string().optional(),
            notifySubscribers: z.boolean().optional(),
            madeForKids: z.boolean().optional(),
          })
          .optional(),
        linkedIn: z
          .object({
            account: z.any().nullable(),
          })
          .optional(),
      }),
    })
    .superRefine((data, ctx) => {
      const ensurePlatformField = ({ condition, path }) => {
        if (!condition) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path,
            message: requiredMessage,
          });
        }
      };

      const descriptionRequired = isGeneralDescriptionRequired({
        selectedPlatforms,
        platformsData: data.platformsData,
      });

      if (descriptionRequired && !data.description?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["description"],
          message: requiredMessage,
        });
      }

      selectedPlatforms.forEach((platform) => {
        if (platform === FACEBOOK_CONSTANT) {
          const platformData = data.platformsData?.facebook || {};
          ensurePlatformField({
            condition: Boolean(platformData.account),
            path: ["platformsData", "facebook", "account"],
          });
          ensurePlatformField({
            condition: Boolean(platformData.contentType),
            path: ["platformsData", "facebook", "contentType"],
          });
        }

        if (platform === INSTAGRAM_CONSTANT) {
          const platformData = data.platformsData?.instagram || {};
          ensurePlatformField({
            condition: Boolean(platformData.account),
            path: ["platformsData", "instagram", "account"],
          });
          ensurePlatformField({
            condition: Boolean(platformData.contentType),
            path: ["platformsData", "instagram", "contentType"],
          });
        }

        if (platform === TIKTOK_CONSTANT) {
          const platformData = data.platformsData?.tiktok || {};
          ensurePlatformField({
            condition: Boolean(platformData.account),
            path: ["platformsData", "tiktok", "account"],
          });
          ensurePlatformField({
            condition: Boolean(platformData.privacy_level),
            path: ["platformsData", "tiktok", "privacy_level"],
          });
          ensurePlatformField({
            condition: Boolean(platformData.contentType),
            path: ["platformsData", "tiktok", "contentType"],
          });
        }

        if (platform === YOUTUBE_CONSTANT) {
          const platformData = data.platformsData?.youtube || {};
          ensurePlatformField({
            condition: Boolean(platformData.account),
            path: ["platformsData", "youtube", "account"],
          });
          ensurePlatformField({
            condition: Boolean(platformData.title?.trim()),
            path: ["platformsData", "youtube", "title"],
          });
          ensurePlatformField({
            condition: Boolean(platformData.privacyStatus?.trim()),
            path: ["platformsData", "youtube", "privacyStatus"],
          });
        }

        if (platform === LINKEDIN_CONSTANT) {
          const platformData = data.platformsData?.linkedIn || {};
          ensurePlatformField({
            condition: Boolean(platformData.account),
            path: ["platformsData", "linkedIn", "account"],
          });
        }
      });
    });
